import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Permission, Role } from '@/types/domain';
import { usePermission } from '@/hooks/usePermission';
import { PortalContext } from '@/config/navigation.config';
import { ROUTES } from '@/constants/routes';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { LoadingState } from '@/components/feedback/LoadingState';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  permissionMatch?: 'all' | 'any';
  requiredRoles?: Role[];
  requiredPortal?: PortalContext;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
  permissionMatch = 'all',
  requiredRoles,
  requiredPortal,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { can, canAll, canAny, hasRole, canAccessPortal } = usePermission();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <LoadingState message="Authenticating enterprise session..." />
      </div>
    );
  }

  // 1. Authentication Check (Redirect to Login)
  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // 2. Lifecycle Status Guard (Blocked, Suspended, Inactive, Locked)
  if (user.status !== 'ACTIVE') {
    return <ForbiddenPage />;
  }

  // 3. Portal-Level Access Guard
  if (requiredPortal && !canAccessPortal(requiredPortal)) {
    return <ForbiddenPage />;
  }

  // 4. Single Permission Guard
  if (requiredPermission && !can(requiredPermission)) {
    return <ForbiddenPage />;
  }

  // 5. Multiple Permissions Guard
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasPerms = permissionMatch === 'any' ? canAny(requiredPermissions) : canAll(requiredPermissions);
    if (!hasPerms) {
      return <ForbiddenPage />;
    }
  }

  // 6. Role-Level Guard
  if (requiredRoles && !hasRole(requiredRoles)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
};
